/**
 * metadataFieldParser - Extracts the stored (Cypher-queryable) field names from
 * the jq metadata definitions returned by the TWG schema endpoints.
 *
 * The TWG schema payload embeds the stored-metadata definitions as jq program
 * strings (e.g. on `dataChangeStreams.updateRecords[].metadata` for nodes and
 * `openSearchObjectMetadata` for relationships). Each top-level object key in
 * that jq program corresponds to a field physically stored in UGS / indexed in
 * OpenSearch — i.e. the fields a Cypher `WHERE` clause can filter on.
 *
 * This is intentionally a lightweight, tolerant parser: it scans for lines that
 * look like `  fieldName:` and filters out jq control keywords. It is shared by
 * the node/relationship queryable-field atoms so the extraction logic lives in a
 * single place.
 */

/**
 * jq control keywords that can appear at the start of an indented line but are
 * not stored field names.
 */
const JQ_KEYWORDS: ReadonlySet<string> = new Set([
	'if',
	'then',
	'else',
	'elif',
	'end',
	'select',
	'map',
	'filter',
	'reduce',
	'as',
	'def',
	'error',
	'try',
	'catch',
]);

/**
 * Matches an indented `fieldName:` line, capturing the field name.
 */
const PROPERTY_LINE_REGEX = /^\s+([a-zA-Z_][a-zA-Z0-9_]+)\s*:/;

/**
 * Parses one or more jq metadata definition strings and returns the de-duplicated,
 * alphabetically sorted set of field names they define.
 *
 * @param jqStrings - jq program strings from the schema payload. `undefined` /
 *   `null` / empty entries are ignored.
 * @returns sorted, de-duplicated list of stored field names.
 */
export function parseMetadataFields(jqStrings: ReadonlyArray<string | undefined | null>): string[] {
	const fields = new Set<string>();

	for (const jq of jqStrings) {
		if (!jq) {
			continue;
		}

		for (const line of jq.split('\n')) {
			const propMatch = line.match(PROPERTY_LINE_REGEX);
			const propName = propMatch?.[1];
			if (propName && !JQ_KEYWORDS.has(propName)) {
				fields.add(propName);
			}
		}
	}

	return Array.from(fields).sort();
}
